import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';
import StudentFullProfileModal from './StudentFullProfileModal';

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function UnpaidEnrollmentsPanel() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [profileRow, setProfileRow] = useState(null);

  useEffect(() => {
    const unsubUsers = onSnapshot(
      query(collection(db, 'users'), where('accessStatus', '==', 'pending_payment')),
      (snap) => setPendingUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))),
      () => setPendingUsers([])
    );
    const unsubSubs = onSnapshot(
      query(collection(db, 'subscriptions'), orderBy('createdAt', 'desc')),
      (snap) => setSubscriptions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setSubscriptions([])
    );
    const unsubPays = onSnapshot(
      query(collection(db, 'payments'), orderBy('createdAt', 'desc')),
      (snap) => setPayments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setPayments([])
    );
    return () => {
      unsubUsers();
      unsubSubs();
      unsubPays();
    };
  }, []);

  const paidEmails = useMemo(() => {
    const set = new Set();
    payments.forEach((p) => {
      if (p.category === 'course' && p.email) set.add(String(p.email).toLowerCase());
    });
    return set;
  }, [payments]);

  const paidUserIds = useMemo(() => {
    const set = new Set();
    payments.forEach((p) => {
      if (p.category === 'course' && p.userId) set.add(p.userId);
    });
    return set;
  }, [payments]);

  const rows = useMemo(() => {
    const fromSignup = pendingUsers.map((u) => ({
      id: `user-${u.uid}`,
      userId: u.uid,
      studentName: u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      email: u.email || '',
      phone: u.phone || '',
      city: u.city || '',
      country: u.country || '',
      registrationNumber: u.registrationNumber || '',
      planLabel: 'Website enrollment — payment pending',
      createdAt: u.createdAt,
      source: 'Website signup',
    }));

    const signupEmails = new Set(fromSignup.map((r) => r.email.toLowerCase()).filter(Boolean));
    const fromSubs = subscriptions
      .filter((sub) => {
        const email = (sub.email || '').toLowerCase();
        if (sub.userId && paidUserIds.has(sub.userId)) return false;
        if (sub.paid) return false;
        if (!email || signupEmails.has(email) || paidEmails.has(email)) return false;
        return (Number(sub.amountPaid) || 0) <= 0;
      })
      .map((sub) => ({
        id: sub.id,
        userId: sub.userId || '',
        studentName: sub.studentName || sub.email,
        email: sub.email || '',
        phone: '',
        city: '',
        country: '',
        registrationNumber: sub.registrationNumber || '',
        planLabel: sub.planLabel || 'GED plan — not paid',
        createdAt: sub.createdAt,
        source: 'Subscription without payment',
      }));

    const q = search.trim().toLowerCase();
    return [...fromSignup, ...fromSubs].filter((row) => {
      if (!q) return true;
      return [row.studentName, row.email, row.phone, row.city, row.registrationNumber, row.planLabel]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [pendingUsers, subscriptions, paidEmails, paidUserIds, search]);

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Enrolled but not paid</h2>
          <p style={s.sub}>
            Students who created an account on the website and have not completed card or cash
            payment. {rows.length} in this list.
          </p>
        </div>
        <input
          type="search"
          placeholder="Search name, email, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={s.search}
        />
      </div>

      {rows.length === 0 ? (
        <div style={s.empty}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, color: '#cbd5e1' }}>
            payments
          </span>
          <p>No unpaid enrollments right now.</p>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Plan</th>
                <th style={s.th}>Enrolled</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={s.tr}>
                  <td style={s.td}>{row.planLabel || 'Enrollment'}</td>
                  <td style={s.td}>{formatDate(row.createdAt)}</td>
                  <td style={s.td}>
                    <span style={s.badge}>Not paid</span>
                    <span style={s.meta}>{row.source}</span>
                  </td>
                  <td style={s.td}>
                    <button type="button" style={s.profileBtn} onClick={() => setProfileRow(row)}>
                      View full profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {profileRow ? (
        <StudentFullProfileModal subscription={profileRow} onClose={() => setProfileRow(null)} />
      ) : null}
    </div>
  );
}

const s = {
  wrap: { flex: 1, minHeight: 0, padding: '24px 28px', overflowY: 'auto' },
  header: { marginBottom: 20 },
  title: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 18,
    fontWeight: 800,
    color: '#4b00d1',
    marginBottom: 6,
  },
  sub: { fontSize: 13, color: '#64748b', maxWidth: 640, lineHeight: 1.6, margin: '0 0 14px' },
  search: {
    width: '100%',
    maxWidth: 420,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
  },
  empty: {
    textAlign: 'center',
    padding: 48,
    color: '#94a3b8',
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e2e8f0',
  },
  tableWrap: {
    overflowX: 'auto',
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '12px 14px', verticalAlign: 'top' },
  name: { display: 'block', color: '#1b365d' },
  meta: { display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 4 },
  metaBlock: { display: 'block' },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    background: '#fff7ed',
    color: '#c2410c',
  },
  profileBtn: {
    padding: '7px 12px',
    borderRadius: 8,
    border: 'none',
    background: '#4b00d1',
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
  },
};
